// BRIEF 86 PHASE 2, STEP 1 (run by brief 90 §5) — the Time entries section, once.
//
// It was 101 lines in `CaseView` and 143 in `CollectiveCaseView`, measured at 63%
// normalised by `scripts/census-cv-ccv-pairs.js`. Every difference between them
// was one of three things, and this component is what is left when all three are
// resolved:
//
//   THE CONTAINER DISCRIMINATOR — `caId` vs `ccId` in the add payload, `kind`,
//   `csType` on the pro-forma handoff. Exempt by the stop test ruled 2026-08-17
//   (both sides select the same KIND of thing), and carried here as `kind`.
//
//   THE HOST'S OWN HANDLES — `om()` vs `globalOm()`, the export's `ca`/`cc`
//   arrays, the file-input ref. Passed in; neither view's is more correct.
//
//   DRIFT — three things CCV was doing that CaseView had already stopped doing,
//   and this is the half worth reading.
//
// ── THE DRIFT, AND CaseView HAD WRITTEN IT DOWN ────────────────────────────
//
// `CaseView.tsx:1020-1027` carries a Phase M+2b comment saying, in its own words,
// that `filterAndShapeTimeEntries` already trims `te` to what the viewer may see,
// that "the legacy visibleTimeEntries client filter is now belt-and-suspenders
// dead weight — dropped", and that "canFin no longer triggers mark-billed UI".
//
// **CollectiveCaseView never got that change.** It still called
// `visibleTimeEntries(...)` over rows the server had already filtered, and still
// passed `canDelegate={canMarkBilled || canFin}`. So the collective case let any
// financial VIEWER raise a pro forma while the regular case required the
// markBilled right AND being RP — and the SERVER's own comment
// (`permissions.js:449`) says a read-only financial grant "confers money
// VISIBILITY, never billing WRITE power". The client contradicted a rule the
// server writes down explicitly.
//
// [RULED 2026-08-17] Creating a pro forma takes markBilled AND RP, on both kinds.
// The narrowing costs nobody the ability — `caseView.timeEntries.markBilled` is
// delegable per case with an expiry (`permissions.js:555` → `isBillingDelegate`)
// and the financial surface is `rp-cases-only` firm-wide (`permissions.js:53`).
// It costs them the ability to do it ungranted.
//
// So `canDelegate` is `canMarkBilled` here, for both, and the visibility filter
// is gone from both: the entries this renders are the entries the server sent.
// 210 §1 — `import React`, the house remedy for react/react-in-jsx-scope
// (SharedUI.tsx says so): the not-loaded wrap is one more JSX element.
import React from "react";
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { Download } from "lucide-react";
import TimeEntryTable from "./TimeEntryTable";
import { exportTimeEntries, ImportPreview } from "./TimeExcel";
import { SectionHeader, AddButton, LockButton, ListNotLoaded } from "./SharedUI";
import { ImportTriggerButton } from "./ImportTriggerButton";
import { FS } from "../lib/tokens";
import type { ToastFn } from "./Toast";

type Entry = {
  id: number;
  caId?: number | null;
  ccId?: number | null;
  billed?: boolean;
  invoiceId?: number | null;
  desc?: string | null;
};
// The props are the union of what the two views pass. Typed as an interface
// rather than `any` because the ratchet allows a NEW file none at all — and the
// discipline pays here: `kind` is the container discriminator and a typo in it
// would otherwise be a silent wrong-matter render.
type Props = {
  /* 150 §1 — 'all' is the spans-matters surface (Dashboard): no `cs`, the
     population is the whole viewer-visible list, and the matter of any given
     row is resolved per row from `ca`/`cc`. */
  kind: "case" | "cc" | "all";
  cs?: { id: number; ref?: string | null } | null;
  co: unknown[];
  currentUser?: number | null;
  te: Entry[];
  sTe: unknown;
  s: (...a: unknown[]) => void;
  SK: Record<string, string>;
  ca: unknown[];
  cc: unknown[];
  // 149 §2.2 — the invoice list, so the export can print the invoice NUMBER
  // where a row carries invoiceId (display information; refused on import).
  // 150 §4 widens the read: the two invoice-level counters read the whole
  // rows the viewer holds (payments, credits, totals, send stamps).
  inv?: {
    id: number;
    number?: string | null;
    paymentStatus?: string | null;
    totalToPay?: number | null;
    sentToClientAt?: string | null;
    sentToPeppolAt?: string | null;
    payments?: { amount: number }[] | null;
    credits?: { amount?: number | null }[] | null;
    // 156 §4 / 158 §1 — the two fields the invoice-level counters read that
    // this declaration had never named: Σ PaymentAllocation (null = not
    // computable, never zero) and the entry-id snapshot a cancelled pro forma
    // keeps after it releases its rows. Union, don't select.
    paidAllocated?: number | null;
    entries?: number[] | null;
  }[];
  // 150 §4 — InvoiceView's canSeeCards generalised: admin, or RP of the
  // matter(s) in scope; the host computes it for its own scope. False means
  // the sums are never computed (absent, never zero).
  canSeeCounters?: boolean;
  // 151D §2(e) — the counters pane's opaque plate, per surface (Dashboard
  // hands the card colour; absent = the table's standing shade).
  counterBg?: string;
  /* 158 §3 — the counter strip stops following this block's lock. Set by the
     Invoices section ALONE: there is nothing to unlock there and the figures
     are the point of the screen. CV, CCV and Dashboard omit it and keep the
     unlock gate exactly as it is. */
  countersAlwaysVisible?: boolean;
  tkTypes: unknown;
  canTerms?: boolean;
  canEditTime?: boolean;
  canMarkBilled?: boolean;
  // 150 §5 — (d) the import/export affordance and (g) the stand-alone pill
  // column, each on its own key (admin · role default · live grant).
  canImportExport?: boolean;
  canPillColumn?: boolean;
  // 144 §4 — the bulk-edit hint + opener, passed through to the table's bulk
  // bar (ruling 3: the controls live there and nowhere else).
  canBulkEdit?: boolean;
  onBulkEdit?: (ids: number[], onDone: (r: unknown) => void) => void;
  // 144 §5 — bulk delete: the hint plus the HOST's confirmAndUndo (the table
  // never mounts its own — one single-slot undo per host).
  canBulkDelete?: boolean;
  confirmAndUndo?: (args: {
    label?: string;
    title?: string;
    message?: string;
    confirmLabel?: string;
    onConfirm: () => void;
    undo?: { snapshot: unknown; restore: (snap: unknown) => void } | null;
    toastName?: string;
    windowMs?: number;
    onExpire?: () => void;
  }) => void;
  /* 150 §1+§2 — THE LOCK AND THE IMPORT LIVE IN THE BLOCK (ruling: host
     state cannot gate a block its hosts do not all have — Dashboard had
     none while CV, CCV and InvoiceView each grew a copy). `nid` feeds the
     block-owned ImportPreview. */
  nid: (arr: unknown[]) => number;
  _roleRows: unknown;
  openTimeModal: (kind: string, payload: Record<string, unknown>) => void;
  onDelete: (row: Entry) => void;
  onProForma: (ids: number[]) => void;
  // Optional: hosts without per-section collapse prefs (InvoiceView) omit
  // them and the section renders open and uncollapsible.
  sCol?: (k: string) => boolean;
  tog?: (k: string) => void;
  // 150 §1 — the dedup review opener, threaded to the table (group (c)).
  onDoubles?: (mode: string) => void;
  // 151C §1 — Invoicing's fifth row (pro forma far left · dedups far right).
  proFormaRow?: boolean;
  // 151C §2 (iii) — the host's tail inside the scroll window (Invoicing's
  // pro forma list follows the entries; reaching it = scroll to bottom).
  afterTable?: ReactNode;
  // 150 §3 — the firm's week for the period filter (resolved axis or the raw
  // settings object; periodBounds accepts either).
  schedAxis?: unknown;
  // 150 §1/§3 — the Dashboard surface's data defaults: open on the viewer
  // ('My time' is a default of the user filter), open on the work week (the
  // card it replaces was week-filtered), and show the case filter (the
  // population spans matters there and nowhere else).
  initialUserFilter?: number | null;
  initialPeriod?: { kind: string; from?: string; to?: string } | null;
  showCaseFilter?: boolean;
  t: (k: string, o?: Record<string, unknown>) => string;
  /* 160 §5 — WIDENED to what `makeToast` actually returns (Toast.tsx). It read
     `(m: string) => void`, which could not express the second argument that
     picks the toast's COLOUR — so the pro-forma refusal below this prop was
     green while saying no. Widened, never cast. */
  toast: ToastFn;
  style?: Record<string, unknown>;
  headerStyle?: Record<string, unknown>;
};

export default function CaseTimeEntriesSection({
  kind, // 'case' | 'cc' — the container discriminator, and the only one
  cs,
  co,
  currentUser,
  te,
  sTe,
  s,
  SK,
  ca,
  cc,
  inv,
  canSeeCounters,
  counterBg,
  countersAlwaysVisible,
  tkTypes,
  canTerms,
  canEditTime,
  canMarkBilled,
  canImportExport,
  canPillColumn,
  canBulkEdit,
  onBulkEdit,
  canBulkDelete,
  confirmAndUndo,
  nid,
  _roleRows,
  openTimeModal, // the host's own opener: om() in CV, globalOm() in CCV
  onDelete, // (row) => void — the host's confirmAndUndo
  onProForma, // (ids) => void
  sCol,
  tog,
  onDoubles,
  proFormaRow,
  afterTable,
  schedAxis,
  initialUserFilter,
  initialPeriod,
  showCaseFilter,
  t,
  toast,
  style,
  headerStyle,
}: Props) {
  const isCc = kind === "cc";
  const isAll = kind === "all";
  // 150 §2 — the block's own lock; boots locked exactly as every host copy did.
  const [locked, setLocked] = useState(true);
  // 150 §1 — the block's own import modal state (nid-handed hosts only).
  const [importFile, setImportFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Drilling into another matter re-locks (the hosts' own effect did this,
  // for every section lock at once; the block does not unmount between
  // matters, so it must do it itself now that the lock is its own). The
  // spans-matters surface has no matter to change; its lock changes only by
  // hand.
  useEffect(() => { setLocked(true); }, [cs?.id]);
  /* ── 148 §1.1, RULED — THE TAB STRIP IS THE ONLY FILTER ────────────────────
     A SECOND, OUTER narrowing stood here: `teBilFilt`, a `useState("unbilled")`
     in each of the three hosts whose SETTER WAS NEVER CALLED in any of them.
     Grepped tree-wide before a line was touched — the only `setTeBilFilt(` in
     the tree is `Dashboard.tsx:2249`, a different state in a different view —
     so in CaseView, CollectiveCaseView and Invoicing this was not a user choice
     and never had been: a hardcoded, invisible, permanently-on *hide everything
     billable*, with no control anywhere to turn it off. `pomMuted`'s shape (147
     §1.6) at a much higher cost.

     WHAT IT COST, and each half was reported as something else:
       · the *Facturable* tab read 0 and STRUCTURALLY COULD NOT read anything
         else — a billable row is `billed` (`server/src/lib/billability.js:13`
         derives `billed === (mark === 'billable')`), and a `billed` row never
         reached the strip;
       · marking a row billable REMOVED IT FROM THE MATTER, which is what was
         reported as "entries DISAPPEAR… now they are DELETED ANYWHERE".
         Nothing was deleted. 147 §1.4's `justMarked` cannot hold a row that has
         left `entries` altogether, so §1.4 is not at fault and is untouched.

     Laurent's own arithmetic is the proof the rows lived: Invoicing mounts the
     table on the UNFILTERED scoped list and read All 405 · Billable 2 on the
     matter where this section read All 403 · Billable 0. 405 − 403 = 2.

     So the entries this renders are the entries the server sent, scoped to this
     matter and nothing else — the server already trimmed `te` to what this
     viewer may see (filterAndShapeTimeEntries); this line is the matter scope,
     and it is the one CCV was missing (see the header). What is unmarked, what
     is unbillable, what is billable and what is invoiced are all in the list,
     and the tab strip chooses which slice is on screen.

     THE DASHBOARD'S `teBilFilt` IS LEFT STANDING and is a different thing: a
     real `PeriodFilter` with four options and live counts that a user drives.
     It becomes redundant with this strip only when 147 §5 gives the Dashboard
     the same block, and that is §5's act. */
  const cte: Entry[] = isAll
    ? te || []
    : (te || []).filter((e) => (isCc ? e.ccId === cs!.id : e.caId === cs!.id));

  return (
    <div
      className="csSec-time"
      style={{ flex: "1 1 100%", minWidth: 0, ...(style || {}) }}
      data-secollapsed={sCol && sCol("time") ? "1" : "0"}
    >
      <SectionHeader
        title={t("case.time_entries")}
        collapsible={!!sCol}
        open={!(sCol && sCol("time"))}
        onToggle={() => tog && tog("time")}
        style={headerStyle}
        leftContent={null}
        rightContent={
          <>
            {/* 188 §F.3 [glance correction, Laurent 2026-09-14] — an add button
                says "Add" (186 (7)): the standard label key, as every other
                section's AddButton reads it. This site read `invoice.te.add_log`
                ("+ Ajouter une prestation"); that row stays seeded, unread.
                (Brief 59's finding stands: CV and CCV read ONE key here, not
                two — `case.add.log_time` was merged away then.) */}
            <AddButton
              label={t("common.add_button.add", { fallback: "Add" })}
              onClick={() =>
                openTimeModal("te", {
                  /* 150 §1 — the spans-matters surface fixes no case: the
                     modal's matter picker is how the row gets one, restricted
                     to the viewer's assignments (the payload the Dashboard
                     card always sent). */
                  ...(isAll
                    ? { _restrictToAssigned: true }
                    : { ...(isCc ? { ccId: cs!.id } : { caId: cs!.id }), _caseFixed: true }),
                  coId: currentUser || "",
                  _lockCo: true,
                  date: new Date().toISOString().slice(0, 10),
                  min: 0,
                  billed: false,
                })
              }
              visible={!locked && canEditTime}
            />
            {/* 151C §1 row 1 — title · Add · lock, NOTHING else: the
                Import/Export pair moved to the tab-strip row (toolbarExtra
                below), still on its own key and the lock. */}
            <LockButton locked={locked} onToggle={() => setLocked((v: boolean) => !v)} />
          </>
        }
      />
      {/* 210 §1 — while the time entries could not load, the block says so in the
          table's place (ListNotLoaded) — never an empty list, never « 0:00 ». */}
      <ListNotLoaded lists={[SK.te]}>
      <TimeEntryTable
        kind={isAll ? "all" : isCc ? "cc" : "case"}
        entries={cte}
        cs={cs || null}
        /* 150 §1 — the spans-matters surface resolves a row's matter per row
           (the table's own 'invoice' discipline): both lists, every spelling. */
        scopedCaseFor={
          isAll
            ? (e: { ccId?: number | null; caId?: number | null; caseId?: number | null }) =>
                e.ccId != null
                  ? (cc as { id?: number }[]).find((c) => c.id === e.ccId) || null
                  : (ca as { id?: number }[]).find((c) => c.id === (e.caId ?? e.caseId)) || null
            : undefined
        }
        co={co}
        currentUser={currentUser}
        schedAxis={schedAxis}
        initialUserFilter={initialUserFilter}
        initialPeriod={initialPeriod}
        showCaseFilter={!!showCaseFilter}
        ca={ca}
        cc={cc}
        canSeeCounters={!!canSeeCounters}
        counterBg={counterBg}
        inv={inv}
        tkTypes={tkTypes}
        canTerms={canTerms}
        /* [RULED 2026-08-17] markBilled AND RP, on both kinds. CCV passed
           `canMarkBilled || canFin` and was the outlier. */
        canDelegate={canMarkBilled}
        canPillColumn={canPillColumn}
        canBulkEdit={canBulkEdit}
        onBulkEdit={onBulkEdit}
        canBulkDelete={canBulkDelete}
        confirmAndUndo={confirmAndUndo}
        te={te}
        sTe={sTe}
        s={s}
        SK={SK}
        locked={locked}
        countersAlwaysVisible={!!countersAlwaysVisible}
        proFormaRow={!!proFormaRow}
        afterTable={afterTable}
        /* 151C §1 row 2 — Import/Export ride the tab-strip row's far right,
           unlocked only, on their 150 §5 (d) key. The machinery (the export
           call, the file input, ImportPreview) stays this section's; only
           the BUTTONS render inside the block. */
        toolbarExtra={
          !locked && canImportExport ? (
            <>
              {/* 148 §1.2, RULED: EXPORT EXPORTS THE WHOLE CASE — `cte`,
                  unfiltered, and that is a choice rather than something that
                  fell out of §1.1. It never followed the tab strip or the
                  block's filters and does not now: a spreadsheet that quietly
                  drops rows is worse than one that holds too many. If a
                  filter is ever given a control again, this line does not
                  follow it. */}
              <button
                className="btn btn-ghost"
                style={{ padding: "2px 7px", fontSize: FS.s10 }}
                onClick={() =>
                  exportTimeEntries({
                    entries: cte,
                    co,
                    roleRows: _roleRows,
                    /* the spans-matters export needs BOTH lists to name a
                       row's matter; a scoped one needs only its own kind's */
                    ca: isAll ? ca : isCc ? [] : ca,
                    cc: isAll || isCc ? cc : [],
                    inv,
                    // 181 (ruling 1) — spaces, not '_'
                    filename: `TimeEntries ${(isAll ? "All" : cs!.ref || (isCc ? "cc" : "case")).replace(/\//g, "-")} ${new Date().toISOString().slice(0, 10)}.xlsx`,
                    toast,
                  })
                }
              >
                <Download size={10} style={{ marginRight: 3, verticalAlign: "middle" }} />
                {t("common.export", { fallback: "Export" })}
              </button>
              <ImportTriggerButton teFileRef={fileRef} t={t} />
            </>
          ) : null
        }
        /* 166 §A — AN EDIT OPENS ITS MATTER AND ITS COLLABORATOR, on every
           surface. This payload used to send `_caseFixed: true` on the two
           scoped surfaces (`TimeEntryModal.tsx:505` turns the picker into a
           locked display) and `_lockCo: true` on all three
           (`TimeEntryModal.tsx:617` does the same to the collaborator field),
           so the one thing an edit could not change was which row it was.
           Laurent's ruling, 2026-09-04: an edit may change its matter and its
           collaborator, like the bulk editor (`BulkTimeEntryModal.tsx:170`
           picks a matter of either kind, `:200` gates the collaborator on
           `_appIsAdmin`). The picker this reaches is 165 §2's both-kinds one,
           so moving a row to a collective case writes `ccId` and clears
           `caId`. The ADD opener (:294) is untouched — a scoped surface still
           fixes the matter of a NEW entry, which is what that surface is for
           — and `!_appIsAdmin` at `TimeEntryModal.tsx:617` still hides the
           collaborator picker from non-admins (C67, server-enforced).

           MEASURED, on a scoped surface: the row LEAVES the list. `cte`
           (:265-267) is `te.filter(e => isCc ? e.ccId === cs.id : e.caId ===
           cs.id)`, so an entry moved to another matter no longer matches the
           scope and the section re-renders without it. Nothing is lost — it
           is in the app-wide `te` and renders on the spans-matters surface
           and on its new matter's own section. */
        onEdit={(e: Entry) => openTimeModal("te", { ...e })}
        onDelete={onDelete}
        onProForma={onProForma}
        onDoubles={onDoubles}
        t={t}
        toast={toast}
      />
      </ListNotLoaded>
      {/* invisible; ImportTriggerButton clicks it through the ref, so its
          place in the tree is immaterial — it lives beside the preview it
          feeds. */}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={(e3: ChangeEvent<HTMLInputElement>) => {
          if (e3.target.files?.[0]) setImportFile(e3.target.files[0]);
          e3.target.value = "";
        }}
      />
      {importFile && (
        <ImportPreview
          file={importFile}
          co={co}
          tkTypes={tkTypes}
          caseId={isAll || isCc ? null : cs!.id}
          ccId={!isAll && isCc ? cs!.id : null}
          te={te}
          sTe={sTe}
          s={s}
          SK={SK}
          nid={nid}
          currentUser={currentUser}
          onClose={() => setImportFile(null)}
          toast={toast}
          matterLabel={cs ? cs.ref ?? `#${String(cs.id)}` : ""} // 214 addendum 4 §25 — the window names the matter
        />
      )}
    </div>
  );
}
